<template>
  <div class="service-only-view">
    <div class="card shadow rounded-3">
      <div class="card-body">
        <!-- Header -->
        <div class="d-flex align-items-center mb-3">
          <div>
            <h6 class="fs-6 fw-bold text-muted mb-1">Service: {{ service_name }}</h6>
            <small class="text-muted text-sm">
              <span v-if="workflow_name">Workflow: {{ workflow_name }}</span>
              <span v-if="workflow_name && service_type"> • </span>
              <span v-if="service_type">{{ format_service_type(service_type) }}</span>
            </small>
          </div>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="text-center p-4">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2 text-muted">Loading service data...</p>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="alert alert-danger" role="alert">
          <i class="bi bi-exclamation-triangle me-2"></i>
          {{ error }}
        </div>

        <!-- Service data content -->
        <div v-else-if="service_data" class="service-content">
          <ServiceData 
            :service="service_data"
            :response_doc="service_response?.response_doc"
            :response="service_response"
            :customer_image="customer_image_base_64"
          />

          <!-- Risk Metrics Section for AML -->
          <div v-if="service_name === 'Anti Money Laundering' && amlNormalized" class="mt-4">
            <div class="card shadow-sm border rounded-3">
              <div class="card-header bg-light">
                <h6 class="mb-0">Risk Metrics</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-4">
                    <div class="text-center">
                      <h4 class="text-danger">{{ nmCount }}</h4>
                      <p class="text-muted mb-0">Negative Media Matches</p>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="text-center">
                      <h4 class="text-warning">{{ pepCount }}</h4>
                      <p class="text-muted mb-0">PEP Matches</p>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="text-center">
                      <h4 class="text-info">{{ wlsCount }}</h4>
                      <p class="text-muted mb-0">Watchlist Matches</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- No data state -->
        <div v-else class="text-center p-4">
          <div class="mb-3">
            <i class="bi bi-info-circle display-4 text-muted"></i>
          </div>
          <h6 class="text-muted">No Service Data Available</h6>
          <p class="text-muted small">Unable to load data for this service.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ServiceData from "@/layouts/data_hub/components/services/service/ServiceData.vue";
import { API_BASE_URL } from "@/config";
import { parseServiceFetchPayload } from "@/layouts/data_hub/components/services/service/serviceFetch";

export default {
  name: "ServiceOnlyView",
  components: {
    ServiceData
  },
  props: {
    service_id: {
      type: [String, Number],
      required: true
    },
    service_name: {
      type: String,
      default: "Service"
    },
    service_type: {
      type: String,
      default: ""
    },
    application_id: {
      type: [String, Number],
      required: false,
      default: null
    },
    workflow_id: {
      type: [String, Number],
      required: false,
      default: null
    },
    workflow_name: {
      type: String,
      default: ""
    },
    entity: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      service_data: null,
      service_response: null,
      customer_image_base_64: null,
      loading: false,
      error: null
    };
  },
  computed: {
    amlNormalized() {
      const res = this.service_response || {};
      if (res.NmResults || res.PepResults || res.WlsResults) return res;
      const dv = res?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes?.DSWatchlistVerification;
      if (dv) {
        return {
          RequestData: res.RequestData || {},
          NmResults: dv.NmResults || [],
          PepResults: dv.PepResults || [],
          WlsResults: dv.WlsResults || [],
        };
      }
      return res;
    },
    nmCount() {
      return this.amlNormalized?.NmResults?.length || 0;
    },
    pepCount() {
      return this.amlNormalized?.PepResults?.length || 0;
    },
    wlsCount() {
      return this.amlNormalized?.WlsResults?.length || 0;
    },
  },
  methods: {
    format_service_type(type) {
      return type ? type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : '';
    },

    async fetch_service_data() {
      if (!this.application_id || !this.service_id) {
        this.error = "Missing application or service information";
        return;
      }

      this.loading = true;
      this.error = null;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        const response = await fetch(
          `${API_BASE_URL}/verify_api/data_hub/applications/${this.application_id}/services/${this.service_id}/`,
          {
            headers: {
              'Authorization': `Token ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch service data: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Parse the service data using the existing utility
        const service_name_lower = (this.service_name || '').toLowerCase();
        const { customer_image, response: parsed_response, response_doc, documents } = parseServiceFetchPayload(
          data, 
          this.service_id, 
          service_name_lower
        );

        // Set up service data object - include documents in the response
        this.service_data = {
          id: this.service_id,
          service_name: this.service_name,
          service_type: this.service_type,
          response: { ...parsed_response, response_doc, documents }
        };
        
        this.service_response = { ...parsed_response, response_doc, documents };
        this.customer_image_base_64 = customer_image;
        
      } catch (error) {
        console.error('Error fetching service data:', error);
        this.error = error.message || 'Failed to load service data';
      } finally {
        this.loading = false;
      }
    }
  },
  
  watch: {
    service_id: {
      handler() {
        this.fetch_service_data();
      },
      immediate: true
    },
    application_id: {
      handler() {
        this.fetch_service_data();
      }
    }
  }
};
</script>

<style scoped>
.service-only-view .service-content {
  min-height: 400px;
}
</style>
